import React, {useEffect, useState, useContext, useMemo, useId} from "react";
import classnames from "classnames";
import {Carousel as ReactCarousel} from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader

import CarouselItem from 'compounds/carousel-item';
import {ENGAGEMENT_DISPLAY_MODE} from 'utils/constants';
import {isHybridCarousel} from 'utils/carousel-helper';
import { AttentionAnimation } from 'utils/AttentionAnimationProvider';
import {useConfig} from "utils/ConfigProvider";

import style from './style.module.scss'


const NO_OF_ELEMENTS_PER_SLIDE = 3

function Carousel({items, creativeAspectRatio, mode = ENGAGEMENT_DISPLAY_MODE.FULL_MODE, onClick, attentionAnimationEnabled, effectType, effectSize, refElement, maxPercentageHeight, inVideo}) {
  const noOfItemsPerSlide = mode === ENGAGEMENT_DISPLAY_MODE.VERTICAL ? 1 : NO_OF_ELEMENTS_PER_SLIDE
  const carouselNeeded = isCarouselNeeded(items, noOfItemsPerSlide);
  const isAnyHybridCarouselSlide = carouselNeeded && isHybridCarousel(items)
  const carouselArrowClasses = getCarouselArrowClasses(mode, isAnyHybridCarouselSlide);

  const [containerHeight, setContainerHeight] = useState(null);
  const instanceId = useId();
  const {
    attentionHovered, 
    setAttentionHovered, 
    attentionHoveredPreviously, 
    setAttentionHoveredPreviously, 
    attentionResetKey, 
    setAttentionResetKey
  } = useContext(AttentionAnimation);

  const {environment} = useConfig();

  const isCreativeStudioPreviewMode = useMemo(() => {
    return environment.isCreativeStudioPreviewMode instanceof Function ? environment.isCreativeStudioPreviewMode() : false;
  }, [environment]);

  // {[slideIndex]: string[]} where each string is the CarouselItem's key. to track images loaded OV-2427
  const [imagesLoadedBySlide, setImagesLoadedBySlide] = useState({});

  const showAttentionAnimation = attentionAnimationEnabled && attentionHovered === null;

  useEffect(() => {
    setAttentionHoveredPreviously(false)
  },[attentionAnimationEnabled, setAttentionHoveredPreviously])

  const optionsCount = items.length;
  useEffect(() => {
    if (isCreativeStudioPreviewMode) {
      setAttentionResetKey(prevKey => prevKey + 1); 
    }
  }, [optionsCount, setAttentionResetKey, isCreativeStudioPreviewMode]);

  // Clean up attention hover on unmount
  useEffect(() => {
    return () => {
      setAttentionHovered(curr => (curr === instanceId ? null : curr)) 
    };
  }, [setAttentionHovered, instanceId]);

  useEffect(() => {
    if (refElement?.current) {
      const heightPercentage = maxPercentageHeight || 1;
      const element = refElement.current;
      const height = element.clientHeight * heightPercentage;

      setContainerHeight(height);
    }
  }, [maxPercentageHeight, refElement]);

  const onImageLoaded = (slideIndex, itemKey) => {
    setImagesLoadedBySlide(prev => {
      const prevKeysForSlide = prev[slideIndex] ?? [];
      // skip if itemKey already present
      if (prevKeysForSlide.includes(itemKey)) {
        return prev;
      }
      return {
        ...prev,
        [slideIndex]: [...prevKeysForSlide, itemKey],
      };
    });
  }

  const renderContent = () => {
    return getItemsPerSlide(noOfItemsPerSlide, items).map((itemsPerSlide, parentIndex) => {
      const isHybrid = isHybridCarousel(itemsPerSlide)
      return (
        <div className={carouselNeeded ? style.carouselSlideContainer : style.carouselContainer} key={parentIndex}>
          {
            itemsPerSlide.map((item, index) => {
              const position = parentIndex * noOfItemsPerSlide + index + 1;
              const itemKey = `${parentIndex}_${items.length}_${index}`;
              return (<div className={classnames(getClasses(items.length, mode, creativeAspectRatio))}
                           key={`${parentIndex}_${index}`}>
                <CarouselItem
                  position={position} mode={mode}
                  key={itemKey}
                  {...getProps(item)}
                  isHybrid={isHybrid}
                  onClick={onClick}
                  containerHeight={containerHeight}
                  // OV-2427 wait for all images to have loaded for this item's slide before enabling attention animation, >= for studio mode where we can add/remove items
                  showAttentionAnimation={imagesLoadedBySlide[parentIndex]?.length >= itemsPerSlide.length ? showAttentionAnimation : false}
                  delayInitialAttentionAnimation={attentionHoveredPreviously}
                  delayInitialAttentionAnimationForInVideo={inVideo}
                  onMouseOver={() => {
                    setAttentionHovered(instanceId); 
                    setAttentionHoveredPreviously(true)
                  }}
                  onMouseOut={() => setAttentionHovered(null)}
                  index={index}
                  resetKey={attentionResetKey}
                  effectType={effectType}
                  effectSize={effectSize}
                  onImageLoaded={() => onImageLoaded(parentIndex, itemKey)}
                />
              </div>)
            })
          }
        </div>
      )
    });
  }

  return carouselNeeded ? <ReactCarousel
    width={'100%'}
    showArrows={true}
    infiniteLoop={true}
    swipeable={true}
    emulateTouch={true}
    showStatus={false}
    showThumbs={false}
    showIndicators={false}
    transitionTime={500}
    renderArrowPrev={(onClickHandler, hasPrev, label) =>
      hasPrev && (
        <button
          className={classnames(carouselArrowClasses)}
          type="button" onClick={onClickHandler} title={label} style={{left: '1.5%'}}>
          <i className={style.carouselControlLeft} aria-hidden="true"/>
        </button>
      )
    }
    renderArrowNext={(onClickHandler, hasNext, label) =>
      hasNext && (
        <button
          className={classnames(carouselArrowClasses)}
          type="button" onClick={onClickHandler} title={label} style={{right: '1.5%'}}>
          <i className={style.carouselControlRight} aria-hidden="true"/>
        </button>
      )
    }
  >
    {renderContent()}
  </ReactCarousel> : renderContent();
}

const getClasses = (itemsCount, mode, creativeAspectRatio) => {
  const classes = [style.carouselItemWrapper];
  switch (creativeAspectRatio) {
    case 'FourByThree':
      classes.push(style.fourByThree);
      break;
    case 'SixteenByNine':
      classes.push(style.sixteenByNine);
      break;
    default:
      break;
  }
  classes.push(style[`count${itemsCount}`]);
  mode === ENGAGEMENT_DISPLAY_MODE.HORIZONTAL && classes.push(style.horizontalDisplay)
  mode === ENGAGEMENT_DISPLAY_MODE.VERTICAL && classes.push(style.verticalDisplay)
  return classes;
}

function isCarouselNeeded(items, noOfItemsPerSlide) {
  return noOfItemsPerSlide > 0 && items.length > noOfItemsPerSlide
}

function getProps(item) {
  if (!item) {
    return {}
  }
  const {
    imageSrcUrl,
    itemId,
    adUnitId,
    imageLandingSrcUrl,
    titleFontSettings,
    layoutType,
    buttonColor,
    title,
    itemType,
    optionIndex,
  } = item
  return {
    imageSrcUrl,
    itemId,
    adUnitId,
    imageLandingSrcUrl,
    titleFontSettings,
    layoutType,
    buttonColor,
    title,
    itemType,
    optionIndex,
  }
}

function getItemsPerSlide(noOfItemsPerSlide, items) {
  const temp = [].concat.apply([],
    items.map((elem, i) => {
      return i % noOfItemsPerSlide ? [] : [items.slice(i, i + noOfItemsPerSlide)];
    })
  );
  if (items.length <= noOfItemsPerSlide) {
    return temp;
  }
  // Fill remaining elements with null
  const lastArray = temp[temp.length - 1]
  for (let index = lastArray.length; index < noOfItemsPerSlide; index++) {
    lastArray[index] = null
  }
  return temp;
}

function getCarouselArrowClasses(mode, isAnyHybridCarouselSlide) {
  return {
    [style.carouselControlButton]: true,
    [style.horizontalDisplay]: mode === ENGAGEMENT_DISPLAY_MODE.HORIZONTAL,
    [style.verticalDisplay]: mode === ENGAGEMENT_DISPLAY_MODE.VERTICAL,
    [style.hybrid]: isAnyHybridCarouselSlide
  }
}

export default Carousel;
